import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Enum,
  RPMVendor,
  RPMVendorDeviceType,
  States,
} from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Address,
  BaseEnum,
  CommunityResource,
  Patient,
  RPMVendorModel,
} from '@vestahealthcare/common/models';
import RPMVendorDevice from '@vestahealthcare/common/models/RPMVendorDevice';
import { EMPTY } from '@vestahealthcare/common/utils/constants';
import {
  isValidAddress,
  isValidCity,
  isValidZipCode,
} from '@vestahealthcare/common/utils/format';
import { formatPhone, parse } from '@vestahealthcare/common/utils/phone';

import {
  Button,
  CheckboxWithLabel,
  CollapsableSidebar,
  Label,
  Modal,
  Select,
  Spinner,
  TextInput,
  Tooltip,
} from 'styleguide-v2';

import { useSelector } from 'dash/src/redux/store';
import CacheServices from 'dash/src/services/CacheServices';
import { fetchMemberCommunityResources } from 'dash/src/services/CommunityResourcesServices';
import { OrderDeviceParams } from 'dash/src/services/RPMVendorServices';
import { useFlags } from 'dash/src/utils/flagUtils';

interface Props {
  member: Patient;
  rpmVendor?: RPMVendor;
  onClose: () => void;
  onSubmit: (params: Omit<OrderDeviceParams, 'memberId' | 'taskId'>) => void;
  open: boolean;
}

export const OrderDeviceModal = ({
  member,
  onClose,
  onSubmit,
  open,
  rpmVendor: defaultRPMVendor,
}: Props) => {
  const { showOrderMedicalGuardianDevice } = useFlags();
  const { pathname } = useLocation();
  const isTaskDashboard = pathname.includes('dashboard/tasks');

  const [rpmVendor, setRPMVendor] = useState<RPMVendor | undefined>(
    showOrderMedicalGuardianDevice ? defaultRPMVendor : RPMVendor.SMART_METER,
  );

  const storeVendor = useSelector((state) => {
    if (!showOrderMedicalGuardianDevice) return RPMVendor.SMART_METER;
    if (isTaskDashboard) return;
    const hasUPMC = !!state.memberInfoSlice.communityResources?.find(
      ({ active, communityResource: { mltcPlan } }) =>
        active && mltcPlan?.mltcCode === CommunityResource.MLTC_PLAN_CODE_UPMC,
    );
    if (hasUPMC) return RPMVendor.MEDICAL_GUARGIAN;
    return RPMVendor.SMART_METER;
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const [models, setModels] = useState<RPMVendorModel[]>([]);
  const [shippingMethods, setShippingMethods] = useState<BaseEnum[]>([]);
  const [selectedModels, setSelectedModels] = useState<RPMVendorModel[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<{
    [x: string]: RPMVendorDevice;
  }>({});

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  const [address1, setAddress1] = useState<string>();
  const [address2, setAddress2] = useState<string>();
  const [city, setCity] = useState<string>();
  const [state, setState] = useState<States>();
  const [zip, setZIP] = useState<string>();

  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');

  const [shippingMethod, setShippingMethod] = useState<BaseEnum>();

  const checkRPMVendor = async () => {
    if (!showOrderMedicalGuardianDevice) {
      setRPMVendor(RPMVendor.SMART_METER);
      return;
    }

    if (!open || !member) return;
    if (storeVendor) {
      setRPMVendor(storeVendor);
      return;
    }

    setLoadingData(true);
    const { items } = await fetchMemberCommunityResources(member.id, {
      mltcPlanCode: CommunityResource.MLTC_PLAN_CODE_UPMC,
      status: 'ACTIVE',
    });

    if (items.length > 0) {
      setRPMVendor(RPMVendor.MEDICAL_GUARGIAN);
    } else {
      setRPMVendor(RPMVendor.SMART_METER);
    }
  };

  const getVendorData = async () => {
    if (!open || !rpmVendor?.serverValue) return;

    setLoadingData(true);
    const [models, methods] = await Promise.all([
      CacheServices.getVendorModels(rpmVendor.serverValue),
      CacheServices.getVendorShippingMethods(rpmVendor.serverValue),
    ]);
    setModels(models);
    setShippingMethods(methods);
    setLoadingData(false);
  };

  useEffect(() => {
    checkRPMVendor();
  }, [open, member, storeVendor]);

  useEffect(() => {
    getVendorData();
  }, [open, rpmVendor]);

  const validateAddress = () =>
    isValidAddress({
      address1: address1 || '',
      address2: address2 || '',
      city: city || '',
      state,
      zipCode: zip || '',
    } as Address);

  const validate = () => {
    setSubmitted(true);

    if (!firstName || !lastName) return;
    if (!validateAddress()) return;
    if (
      !selectedModels?.length ||
      !!selectedModels.find(
        ({ id, devices }) => devices?.length > 1 && !selectedDevices[`${id}`],
      )
    )
      return;
    if (!shippingMethod) return;
    if (rpmVendor === RPMVendor.MEDICAL_GUARGIAN && !phone) return;

    setShowConfirmModal(true);
  };

  const onSave = async () => {
    if (!rpmVendor?.serverValue) return;

    setLoading(true);
    try {
      await onSubmit({
        address: {
          address1,
          address2,
          city,
          state,
          zipCode: zip,
        } as Address,
        deviceId: selectedModels
          .map(({ id, devices }) =>
            devices.length > 1 ? selectedDevices[`${id}`]?.id : devices[0].id,
          )
          .filter(Boolean),
        firstName,
        lastName,
        shippingMethod: shippingMethod?.id as string,
        vendorId: rpmVendor.serverValue,

        // MED GUARDIAN fields
        email: rpmVendor === RPMVendor.MEDICAL_GUARGIAN ? email : undefined,
        phone: rpmVendor === RPMVendor.MEDICAL_GUARGIAN ? phone : undefined,
      });
      onClose();
    } finally {
      setShowConfirmModal(false);
      setLoading(false);
    }
    return true;
  };

  useEffect(() => {
    if (open) {
      setSelectedModels([]);
      setSelectedDevices({});
      setSubmitted(false);
      setLoading(false);

      setFirstName(member.firstName);
      setLastName(member.lastName);

      setAddress1(member.address.address1);
      setAddress2(member.address.address2);
      setCity(member.address.city);
      setState(member.address.state);
      setZIP(member.address.zipCode);

      setEmail(member.email?.email || '');
      setPhone(member.phoneNumber || '');
    }
  }, [open, member]);

  useEffect(() => {
    setRPMVendor(
      showOrderMedicalGuardianDevice ? defaultRPMVendor : RPMVendor.SMART_METER,
    );
  }, [defaultRPMVendor]);

  useEffect(() => {
    if (shippingMethods?.length === 1) {
      setShippingMethod(shippingMethods[0]);
    } else {
      setShippingMethod(undefined);
    }
  }, [shippingMethods]);

  if (!rpmVendor?.value) return <></>;

  return (
    <>
      <CollapsableSidebar
        title={
          <h2>{translate(`tasks.orderDeviceTitle.${rpmVendor.value}`)}</h2>
        }
        onClose={onClose}
        open={open}
        size={550}
      >
        <CollapsableSidebar.Body>
          {loadingData ? (
            <div className="flex center">
              <Spinner width={48} />
            </div>
          ) : (
            <div className="grid-wrapper no-gap">
              <Label className="grid-span-12">
                {translate('tasks.devices')}
              </Label>
              {models.map((model) => {
                const checked = !!selectedModels?.find(
                  ({ id }) => id === model.id,
                );
                return (
                  <Fragment key={`order-device-${model.vendorId}`}>
                    <CheckboxWithLabel
                      className={`grid-span-${
                        checked && model.devices?.length > 1 ? 6 : 12
                      }`}
                      label={
                        <span className="flex gap">
                          <Tooltip text={model.type.toString()}>
                            <i
                              className={`icon-vesta ${RPMVendorDeviceType.getIcon(
                                model.type,
                              )}`}
                            />
                          </Tooltip>
                          {model.name}
                        </span>
                      }
                      checked={checked}
                      onChange={(checked) =>
                        setSelectedModels(
                          checked
                            ? [...selectedModels, model]
                            : [
                                ...selectedModels.filter(
                                  ({ id }) => id !== model.id,
                                ),
                              ],
                        )
                      }
                    />
                    {checked && model.devices?.length > 1 && (
                      <Select
                        className="grid-span-6"
                        disableClearable
                        error={
                          submitted &&
                          checked &&
                          !selectedDevices[`${model.id}`]
                        }
                        getItemLabel={(item: RPMVendorDevice) =>
                          item?.size.toString()
                        }
                        items={model.devices}
                        onChange={(val: RPMVendorDevice) =>
                          setSelectedDevices({
                            ...selectedDevices,
                            [`${model.id}`]: val,
                          })
                        }
                        placeholder={translate('tasks.size')}
                        size="xs"
                        value={selectedDevices[`${model.id}`]}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          )}
          {!loadingData && (
            <>
              <div className="grid-wrapper fit">
                <TextInput
                  className="grid-span-6"
                  data-cy="order-device-first-name"
                  error={submitted && !firstName}
                  label={translate('personalDetails.firstName')}
                  onChange={(value) => setFirstName(value || '')}
                  value={firstName}
                  size="small"
                />
                <TextInput
                  className="grid-span-6"
                  data-cy="order-device-last-name"
                  error={submitted && !lastName}
                  label={translate('personalDetails.lastName')}
                  onChange={(value) => setLastName(value || '')}
                  value={lastName}
                  size="small"
                />
                {rpmVendor === RPMVendor.MEDICAL_GUARGIAN && (
                  <>
                    <TextInput
                      className="grid-span-6"
                      data-cy="order-device-phone"
                      error={submitted && !phone}
                      label={translate('personalDetails.phoneNumber')}
                      onChange={(val?: string) => {
                        let number = val;
                        if (val && val.length > 1) {
                          number =
                            parse(val)?.getNationalNumber()?.toString() || '';
                          number = [...number].slice(0, 10).join('');
                        }
                        setPhone(number || '');
                      }}
                      value={phone ? formatPhone(phone) : undefined}
                      size="small"
                    />
                    <TextInput
                      className="grid-span-6"
                      data-cy="order-device-email"
                      label={translate('personalDetails.email')}
                      onChange={(value) => setEmail(value || '')}
                      value={email}
                      size="small"
                    />
                  </>
                )}
                <TextInput
                  className="grid-span-12"
                  data-cy="order-device-address-1"
                  error={submitted && !validateAddress()}
                  label={translate('personalDetails.addressModal.address1')}
                  onChange={setAddress1}
                  value={address1}
                  size="small"
                />
                <TextInput
                  className="grid-span-12"
                  data-cy="order-device-address-2"
                  label={translate('personalDetails.addressModal.address2')}
                  onChange={setAddress2}
                  value={address2}
                  size="small"
                />
                <TextInput
                  className="grid-span-6"
                  data-cy="order-device-city"
                  error={
                    (submitted &&
                      city &&
                      !isValidCity(city) &&
                      translate('global.cityFormatError')) ||
                    (submitted && !validateAddress())
                  }
                  label={translate('personalDetails.addressModal.city')}
                  onChange={setCity}
                  value={city}
                  size="small"
                />
                <Select
                  className="grid-span-3"
                  data-cy="care-team-state"
                  disableClearable
                  error={submitted && !validateAddress()}
                  getItemLabel={(item: Selectable) => item.value}
                  items={Enum.toSelectable(States.asArray)}
                  label={translate('personalDetails.addressModal.state')}
                  onChange={(val?: Selectable) => {
                    const state = val ? States.byKey[val.value] : undefined;
                    setState(state);
                  }}
                  value={state ? Enum.toSelectable([state])[0] : undefined}
                  size="small"
                />
                <TextInput
                  className="grid-span-3"
                  data-cy="order-device-zip"
                  error={
                    (submitted &&
                      zip &&
                      !isValidZipCode(zip) &&
                      translate('global.zipFormatError')) ||
                    (submitted && !validateAddress())
                  }
                  label={translate('personalDetails.addressModal.zip')}
                  onChange={setZIP}
                  value={zip}
                  size="small"
                />
              </div>
              <div className="grid-span-12" />
              <Select
                className="grid-span-12"
                error={submitted && !shippingMethod}
                items={shippingMethods}
                label={translate('tasks.shippingMethod')}
                onChange={setShippingMethod}
                size="small"
                value={shippingMethod}
              />
              <br />
            </>
          )}
        </CollapsableSidebar.Body>
        <CollapsableSidebar.Buttons>
          <Button color="tertiary" data-cy="add-task-close" onClick={onClose}>
            {translate('global.close')}
          </Button>
          <Button
            color="secondary"
            data-cy="add-task-submit"
            disabled={
              !selectedModels.length ||
              !!selectedModels.find(
                ({ id, devices }) =>
                  devices?.length > 1 && !selectedDevices[`${id}`],
              ) ||
              !shippingMethod ||
              (rpmVendor === RPMVendor.MEDICAL_GUARGIAN && !phone)
            }
            loading={loading}
            onClick={validate}
          >
            {translate('tasks.placeOrder')}
          </Button>
        </CollapsableSidebar.Buttons>
      </CollapsableSidebar>
      <Modal
        title={translate('global.warning')}
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={onSave}
      >
        <p>{translate(`tasks.confirmOrderDevice.${rpmVendor.value}`)}</p>
        <hr />
        <div className="grid-wrapper">
          <span className="grid-span-3">{translate('tasks.devices')}: </span>
          <span className="grid-span-9 semi-bold">
            {selectedModels
              .map(({ id, name, devices }) =>
                devices.length > 1
                  ? `${name} (${selectedDevices[`${id}`]?.size?.toString()})`
                  : name,
              )
              ?.filter(Boolean)
              .join(', ')}
          </span>
          <span className="grid-span-3">{translate('tasks.fullName')}: </span>
          <span className="grid-span-9 semi-bold">
            {firstName} {lastName}
          </span>
          {rpmVendor === RPMVendor.MEDICAL_GUARGIAN && (
            <>
              <span className="grid-span-3">
                {translate('personalDetails.phoneNumber')}:{' '}
              </span>
              <span className="grid-span-9 semi-bold">
                {formatPhone(phone)}
              </span>
              <span className="grid-span-3">
                {translate('personalDetails.email')}:{' '}
              </span>
              <span className="grid-span-9 semi-bold">{email || EMPTY}</span>
            </>
          )}
          <p className="grid-span-3">{translate('tasks.address')}:</p>
          <div className="grid-span-9">
            <p className="no-margin semi-bold">{address1}</p>
            {address2 && <p className="no-margin semi-bold">{address2}</p>}
            <p className="no-margin semi-bold">
              {city}, {state?.value}, {zip}
            </p>
          </div>
          <span className="grid-span-3">
            {translate('tasks.shippingMethod')}:{' '}
          </span>
          <span className="grid-span-9 semi-bold">
            {shippingMethod?.description}
          </span>
        </div>
      </Modal>
    </>
  );
};

export default OrderDeviceModal;
